import React, { useEffect, useRef, useMemo } from 'react';
import './GlobalTieupsSection.css';

const GlobalTieupsSection = () => {
  const canvasRef = useRef(null);

  // ✅ Memoize the countries array to avoid re-creation on each render
  const countries = useMemo(() => [
    { name: 'USA', x: 20, y: 30 },
    { name: 'Canada', x: 18, y: 20 },
    { name: 'UK', x: 48, y: 25 },
    { name: 'Ireland', x: 46, y: 24 },
    { name: 'Germany', x: 51, y: 28 },
    { name: 'France', x: 49, y: 32 },
    { name: 'Spain', x: 48, y: 36 },
    { name: 'Italy', x: 52, y: 36 },
    { name: 'UAE', x: 58, y: 42 },
    { name: 'India', x: 68, y: 45 },
    { name: 'China', x: 75, y: 35 },
    { name: 'Japan', x: 82, y: 35 },
    { name: 'South Korea', x: 80, y: 36 },
    { name: 'Singapore', x: 75, y: 52 },
    { name: 'Australia', x: 82, y: 70 },
    { name: 'Brazil', x: 32, y: 65 },
    { name: 'Mexico', x: 18, y: 42 },
    { name: 'Argentina', x: 30, y: 75 },
    { name: 'South Africa', x: 52, y: 72 },
    { name: 'Egypt', x: 54, y: 40 },
    { name: 'Russia', x: 60, y: 20 },
    { name: 'Poland', x: 52, y: 26 },
    { name: 'Netherlands', x: 49, y: 26 },
    { name: 'Sweden', x: 52, y: 18 },
    { name: 'Norway', x: 50, y: 15 },
    { name: 'Denmark', x: 50, y: 22 },
    { name: 'Switzerland', x: 50, y: 30 },
    { name: 'Austria', x: 52, y: 30 },
    { name: 'Turkey', x: 56, y: 36 },
    { name: 'Israel', x: 56, y: 40 },
    { name: 'Thailand', x: 72, y: 48 },
    { name: 'Vietnam', x: 74, y: 48 },
    { name: 'Malaysia', x: 74, y: 52 },
    { name: 'Indonesia', x: 76, y: 54 },
    { name: 'Philippines', x: 78, y: 48 },
    { name: 'New Zealand', x: 88, y: 75 }
  ], []); // 👈 dependencies empty, so it won’t change

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = (canvas.width = canvas.offsetWidth);
    const height = (canvas.height = canvas.offsetHeight);

    // Background map fill
    ctx.fillStyle = 'rgba(10, 14, 39, 0.5)';
    ctx.fillRect(0, 0, width, height);

    // Grid lines
    ctx.strokeStyle = 'rgba(102, 126, 234, 0.1)';
    ctx.lineWidth = 1;
    for (let i = 0; i < width; i += 50) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    for (let i = 0; i < height; i += 50) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(width, i);
      ctx.stroke();
    }

    // Connections
    ctx.lineWidth = 1.5;
    const connectionPairs = [
      [0, 1], [0, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7],
      [8, 9], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14],
      [0, 16], [2, 20], [4, 8], [10, 30], [9, 28], [15, 16],
      [3, 21], [22, 23], [23, 24], [25, 26], [27, 28], [29, 30],
      [31, 32], [32, 33], [33, 34], [18, 19], [17, 15]
    ];

    connectionPairs.forEach(([i, j]) => {
      if (countries[i] && countries[j]) {
        const x1 = (countries[i].x / 100) * width;
        const y1 = (countries[i].y / 100) * height;
        const x2 = (countries[j].x / 100) * width;
        const y2 = (countries[j].y / 100) * height;

        const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
        gradient.addColorStop(0, 'rgba(0, 255, 136, 0.3)');
        gradient.addColorStop(0.5, 'rgba(102, 126, 234, 0.5)');
        gradient.addColorStop(1, 'rgba(0, 255, 136, 0.3)');

        ctx.strokeStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    });
  }, [countries]);

  return (
    <section className="global-tieups-section section">
      <div className="section-content">
        <h2 className="section-title">Global Partnerships</h2>
        <p className="subtitle">Connected across 35+ Countries</p>

        <div className="map-container">
          <canvas ref={canvasRef} className="world-map-canvas"></canvas>
          <div className="country-markers">
            {countries.map((country, index) => (
              <div
                key={index}
                className="country-marker"
                style={{ left: `${country.x}%`, top: `${country.y}%` }}
                title={country.name}
              >
                <div className="marker-pulse"></div>
                <div className="marker-dot"></div>
                <div className="marker-label">{country.name}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="stats-row">
          <div className="stat-box">
            <div className="stat-number">35+</div>
            <div className="stat-label">Countries</div>
          </div>
          <div className="stat-box">
            <div className="stat-number">150+</div>
            <div className="stat-label">Partners</div>
          </div>
          <div className="stat-box">
            <div className="stat-number">500+</div>
            <div className="stat-label">Projects</div>
          </div>
        </div>
      </div>
      <div className="scroll-indicator">
        <div className="mouse">
          <div className="wheel"></div>
        </div>
      </div>
    </section>
  );
};

export default GlobalTieupsSection;
